import * as React from 'react';

import { cn } from '../../utils/cn';
import { Dialog } from '../dialog/dialog';
import { DialogContent } from '../dialog/dialog-content';
import { DialogTitle } from '../dialog/dialog-title';
import { QuestionnaireFooter } from './questionnaire-footer';
import { QuestionnaireProgress } from './questionnaire-progress';
import {
    QUESTIONNAIRE_COLOUR,
    QUESTIONNAIRE_COLUMN_WIDTH,
    type QuestionnaireColumnWidth,
} from './questionnaire-tokens';
import { useStepTransition } from './use-step-transition';

/**
 * The close control has to be legible against whatever is behind it, and that
 * changes: it sits over the photo only on a step that actually renders one, at
 * `md` and up. Everywhere else — the account wall, the outcomes, a split step
 * given no photo, and every step on a phone, where the photo is not rendered —
 * the ground is white and a white cross is simply invisible.
 *
 * The `[&_svg]:` escape is how the size lands: `DialogClose` puts its own size
 * on the icon, and a descendant selector outranks it where a sibling utility
 * would lose the merge.
 */
const CLOSE_BUTTON_BASE =
    'top-5 right-5 z-1 opacity-100 hover:opacity-70 [&_svg]:size-6';

const closeButtonClassName = (overPhoto: boolean) =>
    overPhoto
        ? `${CLOSE_BUTTON_BASE} text-[#181818] md:text-white`
        : `${CLOSE_BUTTON_BASE} text-[#181818]`;

/**
 * `fullScreen` ships with a card's chrome — border, shadow, rounded corners at
 * `sm` and a grid gap. A takeover has none of those. `sm:rounded-none` is
 * required rather than merely tidy: `cn` merges same-prefix utilities only, so
 * an unprefixed `rounded-none` never beats the variant's `sm:rounded-lg`.
 */
const SURFACE_CLASSNAME =
    'gap-0 border-0 bg-white p-0 shadow-none rounded-none sm:rounded-none';

/**
 * `split` is the question layout: rail, answer column, photo.
 *
 * `centred` is everything that is not a question — the account wall, the submit
 * hold and the confirmation. Those carry their own composition and want the
 * whole ground, so the rail, the photo and the step actions stand down and only
 * the brand mark and the legal row remain.
 */
export type QuestionnaireLayout = 'split' | 'centred';

export interface QuestionnaireShellProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * Accessible name for the dialog. Read by screen readers only — the visible
     * heading is `heading`, which the layout places in the left rail.
     */
    title: string;
    /** Identifies the current step. Drives the carry-in and the scroll rewind. */
    stepKey: string;
    /** Question layout, or the bare ground used by the wall and the outcomes. */
    layout?: QuestionnaireLayout;

    /** Flow name in the eyebrow, e.g. `Get Matched`. Omit to hide the eyebrow. */
    progressLabel?: string;
    /** 1-based position of the current step. */
    step?: number;
    /** Total steps in the flow. */
    totalSteps?: number;

    /** The question, set large in the left rail. Unused when `centred`. */
    heading?: React.ReactNode;
    /** Supporting line under the question. */
    subheading?: React.ReactNode;
    /** Merged onto the subheading paragraph when a flow needs different spacing or width. */
    subheadingClassName?: string;
    /** Uppercase micro-label above the answer column, e.g. `who you are`. */
    microLabel?: string;

    /** Full-bleed photo down the right edge. Desktop only. */
    paneImage?: string;
    /** Alternative text for the photo. Decorative by default. */
    paneImageAlt?: string;
    /**
     * Card floated over the photo, e.g. the checkout's order summary. Centred
     * in the pane and desktop-only, because the pane it sits on is.
     */
    paneOverlay?: React.ReactNode;

    /** `wide` for the style grid and the brief; `narrow` for stacked answers. */
    columnWidth?: QuestionnaireColumnWidth;

    /** Brand mark, top left. Supplied by the app so Rocco ships no asset. */
    logo?: React.ReactNode;

    onBack?: () => void;
    onContinue?: () => void;
    backLabel?: string;
    continueLabel?: string;
    showBack?: boolean;
    canContinue?: boolean;
    /** Suppresses dismissal and both actions while a submit is in flight. */
    busy?: boolean;
    /** Replaces the default copyright / Terms / Privacy row. */
    legal?: React.ReactNode;

    /** Hides the footer entirely, for terminal screens that own their actions. */
    hideFooter?: boolean;
    /**
     * Drops Back and Continue but keeps the rule and the legal row. Defaults to
     * true for `centred`, which has never had step actions; a `split` step sets
     * it when it carries no action of its own, such as a submit that is already
     * in flight.
     */
    hideStepActions?: boolean;

    children: React.ReactNode;
    className?: string;
}

/**
 * The full-screen questionnaire surface: a left rail carrying the question, a
 * centre column carrying the answer, and a photo down the right edge.
 *
 * The generous top margin is held back until the viewport is tall enough to
 * spend it. A phone held sideways is wider than the `md` breakpoint but only a
 * few hundred pixels tall, so keying that spacing to width alone would give the
 * question a quarter of the screen to sit above.
 *
 * It takes the whole viewport including the site header. That comes for free
 * from the dialog sitting at `z-modal`, which is above both `sticky` and
 * `fixed` — the two layers the header uses.
 *
 * Presentational only. It owns no answer state and no flow: it renders the step
 * it is given and reports Back and Continue upward.
 */
export const QuestionnaireShell = ({
    open,
    onOpenChange,
    title,
    stepKey,
    layout = 'split',
    progressLabel,
    step,
    totalSteps,
    heading,
    subheading,
    subheadingClassName,
    microLabel,
    paneImage,
    paneImageAlt = '',
    paneOverlay,
    columnWidth = 'narrow',
    logo,
    onBack,
    onContinue,
    backLabel,
    continueLabel,
    showBack,
    canContinue,
    busy = false,
    legal,
    hideFooter = false,
    hideStepActions,
    children,
    className,
}: QuestionnaireShellProps) => {
    const stepRef = useStepTransition(stepKey);
    const isCentred = layout === 'centred';

    const showProgress =
        progressLabel !== undefined &&
        step !== undefined &&
        totalSteps !== undefined;

    // While a submit is in flight there is nothing to come back to, so the
    // surface refuses every dismissal route rather than only hiding the button.
    const blockWhileBusy = (event: Event) => {
        if (busy) event.preventDefault();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                variant="fullScreen"
                spacing="none"
                className={cn(SURFACE_CLASSNAME, className)}
                closeButtonClassName={closeButtonClassName(
                    !isCentred && Boolean(paneImage),
                )}
                closeButton={!busy}
                onEscapeKeyDown={blockWhileBusy}
                onPointerDownOutside={blockWhileBusy}
                onInteractOutside={blockWhileBusy}
            >
                <DialogTitle className="sr-only">{title}</DialogTitle>

                <div className="flex h-dvh w-full overflow-hidden bg-white">
                    <div className="flex min-w-0 flex-1 flex-col px-5 pt-6 pb-6 md:pt-8.75 md:pr-19.25 md:pb-10.25 md:pl-7.25">
                        <div
                            className="flex min-h-0 flex-1 flex-col overflow-y-auto"
                            ref={stepRef}
                        >
                            {logo ? (
                                <div className="shrink-0">{logo}</div>
                            ) : null}

                            {isCentred ? (
                                <div className="flex flex-1 items-center justify-center py-8">
                                    {children}
                                </div>
                            ) : (
                                <div className="flex flex-1 flex-col pt-8 md:flex-row md:gap-29.25 md:pt-12 md:[@media(min-height:900px)]:pt-25">
                                    <div className="shrink-0 md:w-80.25">
                                        {showProgress ? (
                                            <QuestionnaireProgress
                                                label={progressLabel}
                                                step={step}
                                                total={totalSteps}
                                            />
                                        ) : null}

                                        <h2
                                            className={cn(
                                                'mt-4 font-sans text-7 leading-[1.15] font-medium tracking-[-0.03em] md:mt-5 md:text-10 md:leading-[1.1] md:tracking-[-0.04em]',
                                                QUESTIONNAIRE_COLOUR.inkHeading,
                                            )}
                                        >
                                            {heading}
                                        </h2>

                                        {subheading ? (
                                            <p
                                                className={cn(
                                                    'mt-3 font-sans text-3.75 leading-[1.4] font-medium tracking-[-0.02em] md:mt-4.5 md:text-4 md:leading-[1.3]',
                                                    QUESTIONNAIRE_COLOUR.inkMuted,
                                                    subheadingClassName,
                                                )}
                                            >
                                                {subheading}
                                            </p>
                                        ) : null}
                                    </div>

                                    <div className="flex min-w-0 flex-1 flex-col pt-7 md:pt-0">
                                        {microLabel ? (
                                            <p
                                                className={cn(
                                                    'shrink-0 font-sans text-2.75 leading-none font-medium tracking-[0.03em] uppercase',
                                                    QUESTIONNAIRE_COLOUR.inkLabel,
                                                )}
                                            >
                                                {microLabel}
                                            </p>
                                        ) : null}

                                        <div
                                            className={cn(
                                                'mt-5 md:mt-6.25',
                                                QUESTIONNAIRE_COLUMN_WIDTH[
                                                    columnWidth
                                                ],
                                            )}
                                        >
                                            {children}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {hideFooter ? null : (
                            <QuestionnaireFooter
                                className="shrink-0 pt-6 md:pt-8"
                                onBack={onBack}
                                onContinue={onContinue}
                                backLabel={backLabel}
                                continueLabel={continueLabel}
                                showBack={showBack}
                                canContinue={canContinue}
                                busy={busy}
                                hideActions={hideStepActions ?? isCentred}
                                legal={legal}
                            />
                        )}
                    </div>

                    {paneImage && !isCentred ? (
                        <div className="relative hidden w-[33.75%] shrink-0 md:block">
                            {/*
                              The photo is desktop-only furniture, and hiding it
                              with a class would still cost a phone the download.
                              A source that only matches at `md` leaves the image
                              with nothing to load below it.
                            */}
                            <picture>
                                <source
                                    media="(min-width: 768px)"
                                    srcSet={paneImage}
                                />
                                <img
                                    alt={paneImageAlt}
                                    className="size-full object-cover"
                                />
                            </picture>

                            {paneOverlay ? (
                                <div className="absolute inset-0 flex items-center justify-center p-6">
                                    <div className="w-full max-w-full min-w-0">
                                        {paneOverlay}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};
